import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
export function SleepTrackingVisualization() {
  const [score, setScore] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setScore(94), 500);
    return () => clearTimeout(timer);
  }, []);
  const sleepStages = [{
    name: 'Deep Sleep',
    duration: 3.2,
    color: 'bg-indigo-600',
    percentage: 40
  }, {
    name: 'REM Sleep',
    duration: 2.5,
    color: 'bg-purple-500',
    percentage: 31
  }, {
    name: 'Light Sleep',
    duration: 2.3,
    color: 'bg-blue-400',
    percentage: 29
  }];
  return <div className="my-16 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 rounded-2xl p-8 text-white" data-id="element-464">
      <div className="text-center mb-8" data-id="element-465">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-4" data-id="element-466">
          <Moon className="w-4 h-4" data-id="element-467" />
          SLEEP QUALITY TRACKING
        </div>
        <h3 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-468">
          Uninterrupted, Restorative Sleep
        </h3>
        <p className="text-indigo-200" data-id="element-469">
          Track your sleep quality with LyraSleep
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-470">
        {/* Sleep Score Circle */}
        <div className="flex justify-center" data-id="element-471">
          <div className="relative" data-id="element-472">
            <svg className="w-64 h-64 transform -rotate-90" data-id="element-473">
              {/* Background circle */}
              <circle cx="128" cy="128" r="110" stroke="rgba(255,255,255,0.1)" strokeWidth="12" fill="none" data-id="element-474" />
              {/* Progress circle */}
              <motion.circle cx="128" cy="128" r="110" stroke="url(#sleepGradient)" strokeWidth="12" fill="none" strokeLinecap="round" initial={{
              strokeDasharray: '0 691'
            }} animate={{
              strokeDasharray: `${score / 100 * 691} 691`
            }} transition={{
              duration: 2,
              ease: 'easeOut'
            }} data-id="element-475" />
              <defs data-id="element-476">
                <linearGradient id="sleepGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-477">
                  <stop offset="0%" stopColor="#8B5CF6" data-id="element-478" />
                  <stop offset="100%" stopColor="#06B6D4" data-id="element-479" />
                </linearGradient>
              </defs>
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-480">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.5,
              type: 'spring'
            }} className="text-center" data-id="element-481">
                <div className="text-6xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent" data-id="element-482">
                  {score}
                </div>
                <div className="text-sm text-indigo-300 font-semibold" data-id="element-483">
                  Sleep Score
                </div>
                <div className="text-xs text-indigo-400 mt-1" data-id="element-484">Excellent</div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Sleep Stages Breakdown */}
        <div className="space-y-4" data-id="element-485">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10" data-id="element-486">
            <div className="flex items-center justify-between mb-4" data-id="element-487">
              <h4 className="font-bold text-lg" data-id="element-488">Sleep Stages</h4>
              <div className="text-sm text-indigo-300" data-id="element-489">8.0 hours total</div>
            </div>

            {sleepStages.map((stage, index) => <motion.div key={stage.name} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.2
          }} className="mb-4 last:mb-0" data-id="element-490">
                <div className="flex items-center justify-between mb-2" data-id="element-491">
                  <div className="flex items-center gap-2" data-id="element-492">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} data-id="element-493" />
                    <span className="text-sm font-semibold" data-id="element-494">{stage.name}</span>
                  </div>
                  <span className="text-sm text-indigo-300" data-id="element-495">
                    {stage.duration}h
                  </span>
                </div>
                <div className="relative h-2 bg-white/10 rounded-full overflow-hidden" data-id="element-496">
                  <motion.div className={`absolute inset-y-0 left-0 ${stage.color} rounded-full`} initial={{
                width: 0
              }} animate={{
                width: `${stage.percentage}%`
              }} transition={{
                delay: 0.5 + index * 0.2,
                duration: 1
              }} data-id="element-497" />
                </div>
              </motion.div>)}
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-498">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-499">
              <div className="text-2xl font-bold text-cyan-400" data-id="element-500">0</div>
              <div className="text-xs text-indigo-300" data-id="element-501">Wake-ups</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10" data-id="element-502">
              <div className="text-2xl font-bold text-green-400" data-id="element-503">98%</div>
              <div className="text-xs text-indigo-300" data-id="element-504">Sleep Efficiency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10" data-id="element-505">
        <div className="flex items-center justify-between mb-4" data-id="element-506">
          <div className="flex items-center gap-2" data-id="element-507">
            <Moon className="w-4 h-4 text-indigo-400" data-id="element-508" />
            <span className="text-sm font-semibold" data-id="element-509">10:30 PM</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-510">
            <Sun className="w-4 h-4 text-yellow-400" data-id="element-511" />
            <span className="text-sm font-semibold" data-id="element-512">6:30 AM</span>
          </div>
        </div>
        <div className="relative h-12 bg-white/10 rounded-lg overflow-hidden" data-id="element-513">
          <motion.div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-500 to-blue-400" initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 1,
          duration: 1.5
        }} style={{
          transformOrigin: 'left'
        }} data-id="element-514" />
          <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold" data-id="element-515">
            Uninterrupted Sleep
          </div>
        </div>
      </div>
    </div>;
}